/**
 * Replay gate: judge whether a cache-only workflow run counts as a CLEAN
 * full-replay convergence proof.
 *
 * Pure, dependency-free (no DB, no Effect, no IO) — this is the canonical
 * verdict function the future cloud verifier service and the CLI's
 * cache-convergence-loop display logic both call. See
 * `docs/improvement-loops/manual-cache-convergence.md` for the authoritative
 * proof-gate prose this codifies; where this module and that prose disagree,
 * the runbook is the source of truth and this file should be updated to match.
 *
 * Login exemption doctrine: cache-only mode proves deterministic replay of
 * WORKFLOW STEPS (actions/assertions). Login/session bootstrap is
 * infrastructure, not a step under test — a cache-only run always falls back
 * to the same agent login path a normal run uses when the deterministic
 * session is cold/expired (see `apps/api/src/workflows/execution/
 * cache-only-abort.ts`'s module doc and the three login call sites it used to
 * gate). An agentic login must never, by itself, make an otherwise-clean run
 * count as unclean. This gate enforces that by never treating `nodeType:
 * 'login'` steps as cache-managed (excluded from `step_not_replayed`) and by
 * explicitly excluding them from the `heal_or_recovery_used` check, which
 * otherwise runs across every step regardless of type. `workflowAgentRunCount`
 * (see {@link ReplayGateRunInput}) is likewise defined to exclude login's
 * agent runs from the `workflow_llm_spend` violation. This exemption is
 * scoped to login ONLY — a non-login step reported with agent/heal/recovery
 * involvement is still a violation.
 *
 * @module replay-gate/judge-run
 */

import type {
  ReplayGateAssertionDefect,
  ReplayGateNodeConfigInput,
  ReplayGateRunInput,
  ReplayGateStepInput,
  ReplayGateVerdict,
  ReplayGateViolation,
} from './types.js';

/**
 * Node types that legitimately emit `cacheOutcome`/`cacheHitSource` and are
 * therefore "cache-managed" for gate purposes.
 *
 * Mirrors `CACHEABLE_NODE_TYPES` in
 * `packages/db/src/repositories/cache-telemetry.ts` (~line 41). Duplicated
 * here (not imported) because a DB-package import would invert this
 * package's dependency direction — `@chatsdet/workflow-cache` is a leaf
 * package `packages/db` itself may eventually want to depend on. Keep this
 * set in sync with the DB constant by hand.
 */
const CACHE_MANAGED_NODE_TYPES: ReadonlySet<string> = new Set(['action', 'assertion']);

/**
 * Login/session bootstrap is infrastructure, not a cached workflow step under
 * test — see this module's "Login exemption doctrine" doc comment above.
 * `'login'` is deliberately absent from {@link CACHE_MANAGED_NODE_TYPES}
 * (so `step_not_replayed` never applies to it) and is explicitly excluded
 * from the `heal_or_recovery_used` check below, which otherwise runs across
 * every step regardless of type.
 */
const LOGIN_NODE_TYPE = 'login';

/**
 * The single terminal-success value for `workflow_runs.status`
 * (`workflowStatusEnum` in `packages/db/src/schema/enums.ts`: queued /
 * running / success / failed / inconclusive / canceled / timeout).
 */
const RUN_SUCCESS_STATUS = 'success';

/**
 * `cacheHitSource` values that mean "an agent or a repair path was involved,"
 * never acceptable in a clean cache-only proof — checked across ALL steps,
 * not just cache-managed ones, per the gate's "no `l2_gap` or `recovery`
 * after its prove pass" rule.
 */
const HEAL_OR_RECOVERY_SOURCES: ReadonlySet<string> = new Set(['l2_gap', 'recovery']);

/**
 * `cacheHitSource` values accepted as deterministic replay for a
 * cache-managed step, alongside the baseline `'replay'`.
 *
 * `mcp_replay` decision (evidence, not assumption): `apps/api/src/workflows/
 * execution/execute-mcp-node.ts` sets `cacheHitSource: 'mcp_replay'` only on
 * the branch that calls `replayMcpNode({ cacheRow: { actionsJson,
 * codifiedAssertion }, ... })` — a deterministic replay of the *recorded*
 * cached MCP tool-call sequence, gated behind the same `cacheEligible` /
 * `findValidCacheByHash` lookup as the standard action/assertion replay path,
 * and explicitly NOT the agent branch (which runs when `!cacheRow` and is
 * rejected outright under `options.cacheOnly` via `abortCacheOnly`). So
 * `mcp_replay` is genuinely deterministic cache replay, not agent/heal
 * involvement, and is accepted here.
 *
 * Caveat: `mcp_replay` is only ever emitted for `nodeType: 'mcp'` steps
 * (see `execute-mcp-node.ts` `nodeType: 'mcp'` in its step metadata), and
 * `'mcp'` is NOT in {@link CACHE_MANAGED_NODE_TYPES} (the DB's
 * `CACHEABLE_NODE_TYPES` is `('action','assertion')` only). Under the
 * current canonical cache-managed set this exception is therefore currently
 * inert — no step this gate treats as cache-managed can ever report
 * `mcp_replay`. It's kept here (rather than dropped) so the exception fires
 * correctly if `CACHE_MANAGED_NODE_TYPES` is ever widened to include `mcp`,
 * without silently reclassifying deterministic MCP replay as a violation.
 */
const DETERMINISTIC_REPLAY_HIT_SOURCES: ReadonlySet<string> = new Set(['replay', 'mcp_replay']);

function isCacheManagedStep(step: ReplayGateStepInput): boolean {
  return CACHE_MANAGED_NODE_TYPES.has(step.nodeType);
}

/** Count occurrences of each value — the multiset primitive every assertion-completeness check needs. */
function countBy(values: readonly string[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return counts;
}

/**
 * Terminal status a tolerant-mode run reaches when it collected mechanical
 * assertion failures instead of halting on the first one (see
 * `run-node-cache-replay.ts`'s cache-only fatal-continue).
 */
const RUN_FAILED_STATUS = 'failed';

/**
 * Every FAILED step (any node type, not just cache-managed) must itself
 * carry at least one non-warn `assertionFailures` entry — a failed step with
 * no recorded assertion failure means something else (mechanical error,
 * crash, timeout) made it fail, which the tolerant waiver must never paper
 * over.
 */
function everyFailedStepExplainedByAssertions(steps: readonly ReplayGateStepInput[]): boolean {
  return steps
    .filter((step) => step.status === RUN_FAILED_STATUS)
    .every((step) => (step.assertionFailures ?? []).some((failure) => failure.severity !== 'warn'));
}

/**
 * Every node the gate considers cache-managed for this workflow
 * (`input.nodeConfigs`) must have produced at least one step in this run.
 * Closes the truncation gap: a run that fails mid-flow leaves later nodes
 * with NO step rows at all, so they contribute zero violations on their own
 * — without this check a truncated run could look identical to a run that
 * genuinely reached every node and only failed assertions along the way.
 */
function nodeConfigsMissingSteps(input: ReplayGateRunInput): string[] {
  const stepNodeIds = new Set(input.steps.map((step) => step.nodeId));
  return input.nodeConfigs.filter((nodeConfig) => !stepNodeIds.has(nodeConfig.nodeId)).map((nc) => nc.nodeId);
}

function judgeRunLevel(
  input: ReplayGateRunInput,
  violations: ReplayGateViolation[],
  tolerateAssertionFailures: boolean,
  hasNonWarnAssertionFailure: boolean
): void {
  const missingNodeIds = tolerateAssertionFailures ? nodeConfigsMissingSteps(input) : [];

  const runNotTerminalSuccessWaived =
    tolerateAssertionFailures &&
    input.runStatus === RUN_FAILED_STATUS &&
    input.cacheOnlyFailure == null &&
    hasNonWarnAssertionFailure &&
    everyFailedStepExplainedByAssertions(input.steps) &&
    missingNodeIds.length === 0;

  if (input.runStatus !== RUN_SUCCESS_STATUS && !runNotTerminalSuccessWaived) {
    violations.push({
      code: 'run_not_terminal_success',
      detail: `run status is "${input.runStatus}", expected "${RUN_SUCCESS_STATUS}"`,
    });
  }

  for (const nodeId of missingNodeIds) {
    violations.push({
      code: 'cache_managed_node_not_executed',
      nodeId,
      detail:
        'workflow node is cache-managed for this workflow but produced zero steps in this run — likely a truncated run that failed before reaching it',
    });
  }

  if (input.cacheOnlyFailure != null) {
    violations.push({
      code: 'cache_only_failure_present',
      detail: 'run metadata carries a cacheOnlyFailure report',
    });
  }

  if (!input.cacheOnly) {
    violations.push({
      code: 'not_cache_only_run',
      detail: 'run was not requested as cache-only; cannot serve as a cache-only convergence proof',
    });
  }
}

function judgeSteps(input: ReplayGateRunInput, violations: ReplayGateViolation[]): void {
  const cacheManagedSteps = input.steps.filter(isCacheManagedStep);

  if (cacheManagedSteps.length === 0) {
    violations.push({
      code: 'no_cache_managed_steps',
      // An empty/degenerate run with zero action|assertion steps would
      // otherwise trivially pass every other check — that's a false
      // convergence, not a proof of anything. Flag it explicitly.
      detail: 'run has zero cache-managed (action|assertion) steps — cannot prove convergence',
    });
  }

  for (const step of cacheManagedSteps) {
    const replayedCleanly =
      step.cacheOutcome === 'hit' &&
      step.cacheHitSource !== undefined &&
      DETERMINISTIC_REPLAY_HIT_SOURCES.has(step.cacheHitSource);

    if (!replayedCleanly) {
      violations.push({
        code: 'step_not_replayed',
        nodeId: step.nodeId,
        detail: `cacheOutcome=${step.cacheOutcome ?? 'undefined'} cacheHitSource=${step.cacheHitSource ?? 'undefined'} (expected hit + replay/mcp_replay)`,
      });
    }
  }

  // Checked across ALL steps (not just cache-managed) — a heal/recovery hit
  // source on a step this gate wouldn't otherwise classify as cache-managed
  // is still agent/repair involvement in a run claiming cache-only proof.
  // Login is the sole exception (see this module's "Login exemption
  // doctrine" doc comment): it is infrastructure, not a workflow step under
  // test, so an agentic login must never trip this check.
  for (const step of input.steps) {
    if (step.nodeType === LOGIN_NODE_TYPE) continue;
    if (step.cacheHitSource !== undefined && HEAL_OR_RECOVERY_SOURCES.has(step.cacheHitSource)) {
      violations.push({
        code: 'heal_or_recovery_used',
        nodeId: step.nodeId,
        detail: `step reported cacheHitSource=${step.cacheHitSource}`,
      });
    }
  }
}

/**
 * Config-vs-cache assertion completeness checks for ONE node — pure and
 * usable WITHOUT any run data (no executed/unresolved ids). This is the
 * offline subset of `judgeAssertionCompleteness`'s full check: whether a
 * node's cache row and compiled entries structurally agree with its
 * configured assertions. The run-dependent checks
 * (`assertion_not_executed`, `assertion_double_executed`,
 * `assertion_unresolved`) require an actual executed run and stay in
 * `judgeAssertionCompleteness` below, which delegates to this function for
 * the config-vs-cache subset.
 *
 * Exported for `validate_board`
 * (`apps/api/src/workflows/cache/convergence/tools/index.ts`), which judges
 * every fork node's cache-row completeness offline, before spending any
 * cache-only run.
 */
export function judgeNodeAssertionCompleteness(
  nodeConfig: ReplayGateNodeConfigInput
): ReplayGateViolation[] {
  const violations: ReplayGateViolation[] = [];
  const { nodeId, configuredAssertionIds, cachedAssertActionIds, boundAssertionIds, compiledEntryAssertionIds } =
    nodeConfig;

  const configuredSet = new Set(configuredAssertionIds);
  const cachedCounts = countBy(cachedAssertActionIds);
  const boundSet = new Set(boundAssertionIds);
  const compiledCounts = countBy(compiledEntryAssertionIds);

  for (const assertionId of configuredAssertionIds) {
    const cachedCount = cachedCounts.get(assertionId) ?? 0;
    if (cachedCount === 0) {
      violations.push({
        code: 'assertion_missing_from_cache',
        nodeId,
        assertionId,
        detail: 'configured assertion id is absent from the cache row\'s browser_assert actions',
      });
    } else if (cachedCount > 1) {
      violations.push({
        code: 'assertion_extra_in_cache',
        nodeId,
        assertionId,
        detail: `configured assertion id occurs ${cachedCount} times in cache content, expected exactly once`,
      });
    }
  }

  // Cached assert ids with no matching configured id — extra content the
  // config no longer describes (e.g. a stale/orphaned cached assertion).
  for (const [assertionId] of cachedCounts) {
    if (!configuredSet.has(assertionId)) {
      violations.push({
        code: 'assertion_extra_in_cache',
        nodeId,
        assertionId,
        detail: 'cached browser_assert id has no matching configured inline assertion',
      });
    }
  }

  // Structural compiled-entries placement checks:
  // - A BOUND id (its verify step carries `assertionId`) must compile
  //   at-position into the entries exactly once. Missing = an unfilled gap
  //   that needs an L2 playbook-run to fill — not a pass. Duplicated = a
  //   compiler/consumption-order defect.
  // - An UNBOUND (legacy) id belongs to the residual trailing pass only and
  //   must never appear in compiled entries.
  for (const assertionId of boundSet) {
    const compiledCount = compiledCounts.get(assertionId) ?? 0;
    if (compiledCount === 0) {
      violations.push({
        code: 'bound_assertion_missing_from_compiled_entries',
        nodeId,
        assertionId,
        detail: 'bound assertion id is absent from compiled entries — unfilled gap, not a pass',
      });
    } else if (compiledCount > 1) {
      violations.push({
        code: 'bound_assertion_duplicated_in_compiled_entries',
        nodeId,
        assertionId,
        detail: `bound assertion id appears ${compiledCount} times among compiled entries, expected exactly once`,
      });
    }
  }
  for (const [assertionId] of compiledCounts) {
    if (!boundSet.has(assertionId)) {
      violations.push({
        code: 'unbound_assertion_in_compiled_entries',
        nodeId,
        assertionId,
        detail:
          'assertion id appears in compiled entries but no verify step binds it — it belongs to the residual trailing pass only',
      });
    }
  }

  return violations;
}

function judgeAssertionCompleteness(
  nodeConfig: ReplayGateNodeConfigInput,
  executedIdsByNode: Map<string, string[]>,
  unresolvedIdsByNode: Map<string, string[]>,
  violations: ReplayGateViolation[]
): void {
  const { nodeId, configuredAssertionIds } = nodeConfig;
  const executedCounts = countBy(executedIdsByNode.get(nodeId) ?? []);

  violations.push(...judgeNodeAssertionCompleteness(nodeConfig));

  for (const assertionId of configuredAssertionIds) {
    const executedCount = executedCounts.get(assertionId) ?? 0;
    if (executedCount === 0) {
      violations.push({
        code: 'assertion_not_executed',
        nodeId,
        assertionId,
        detail: 'configured assertion produced zero executed deterministic results in the run',
      });
    } else if (executedCount > 1) {
      violations.push({
        code: 'assertion_double_executed',
        nodeId,
        assertionId,
        detail: `configured assertion executed ${executedCount} times, expected exactly once`,
      });
    }
  }

  for (const assertionId of unresolvedIdsByNode.get(nodeId) ?? []) {
    violations.push({
      code: 'assertion_unresolved',
      nodeId,
      assertionId,
      detail:
        'assertion was skipped/unresolved/downgraded to a warning without executing (e.g. assertion_requires_llm)',
    });
  }
}

/** Options controlling how strictly {@link judgeCacheOnlyRunClean} judges a run. */
export interface JudgeCacheOnlyRunCleanOptions {
  /**
   * When true, a run that reached the end with every step hit/replayed and
   * every assertion having EXECUTED can still count as clean even though some
   * assertions mechanically FAILED (non-warn severity) — the run is only
   * allowed to be `runStatus === 'failed'` for this reason (see
   * `judgeRunLevel`'s waiver), never for a cache-only-failure, a non-replayed
   * step, or an incomplete assertion multiset. Failed assertions are surfaced
   * as `verdict.assertionDefects` instead of blocking the verdict. Defaults
   * to false (strict — no relaxation, matches pre-existing behavior).
   */
  tolerateAssertionFailures?: boolean;
}

/**
 * Judge whether a cache-only workflow run is a CLEAN full-replay convergence
 * proof, per the gate defined in
 * `docs/improvement-loops/manual-cache-convergence.md`. Pure function — collects
 * every violation rather than short-circuiting on the first one, so callers
 * can present a complete diagnosis in one pass.
 */
export function judgeCacheOnlyRunClean(
  input: ReplayGateRunInput,
  options?: JudgeCacheOnlyRunCleanOptions
): ReplayGateVerdict {
  const violations: ReplayGateViolation[] = [];
  const tolerateAssertionFailures = options?.tolerateAssertionFailures ?? false;

  const assertionDefects: ReplayGateAssertionDefect[] = [];
  if (tolerateAssertionFailures) {
    for (const step of input.steps) {
      for (const failure of step.assertionFailures ?? []) {
        if (failure.severity === 'warn') continue;
        assertionDefects.push({
          nodeId: step.nodeId,
          assertionId: failure.assertionId,
          severity: failure.severity,
          ...(failure.detail !== undefined ? { detail: failure.detail } : {}),
        });
      }
    }
  }
  const hasNonWarnAssertionFailure = assertionDefects.length > 0;

  judgeRunLevel(input, violations, tolerateAssertionFailures, hasNonWarnAssertionFailure);
  judgeSteps(input, violations);

  const executedIdsByNode = new Map<string, string[]>();
  const unresolvedIdsByNode = new Map<string, string[]>();
  for (const step of input.steps) {
    if (step.executedAssertionIds && step.executedAssertionIds.length > 0) {
      const existing = executedIdsByNode.get(step.nodeId) ?? [];
      existing.push(...step.executedAssertionIds);
      executedIdsByNode.set(step.nodeId, existing);
    }
    if (step.unresolvedAssertionIds && step.unresolvedAssertionIds.length > 0) {
      const existing = unresolvedIdsByNode.get(step.nodeId) ?? [];
      existing.push(...step.unresolvedAssertionIds);
      unresolvedIdsByNode.set(step.nodeId, existing);
    }
  }

  for (const nodeConfig of input.nodeConfigs) {
    judgeAssertionCompleteness(nodeConfig, executedIdsByNode, unresolvedIdsByNode, violations);
  }

  const spendKnown = input.workflowAgentRunCount !== undefined;
  if (spendKnown && (input.workflowAgentRunCount ?? 0) > 0) {
    violations.push({
      code: 'workflow_llm_spend',
      detail: `${input.workflowAgentRunCount} workflow-attributable agent run(s) logged during the run window`,
    });
  }

  return { clean: violations.length === 0, violations, spendKnown, assertionDefects };
}
